import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import * as blueprint from '@adc/blueprint';
import * as graph from '@adc/sdk';
import classnames from 'classnames/bind';
import { Placeholder } from 'semantic-ui-react';
import Button from '../../elementals/button';
import AddressInfo from '../addressInfo';
import cls from './cardPartsStyles.scss';
import { getAddressLine, getOpeningBidValue, valuationEstimate, getDisplayedValue, MISSING_VALUE_LABEL, shouldDisplayReserveMet, LISTING_CARD_PHOTO_WIDTH, } from './cardHelpers';
import { enums } from '@adc/sdk';
import { ListingBadgesTags } from '../badges';
import PropertySaveIndicator from '../../property/propertySaveIndicator';
import ListingIndicator from '../listingIndicator';
import { useBackgroundImageWithFallback } from '../../../hooks/useBackgroundImageWithFallback';
import { formatListingImgUrl } from '../utils/formatters';
import SmartSaleLogo from '../../../images/smartsale-logo.png';
const cx = classnames.bind(cls);
export const linkPropType = PropTypes.oneOfType([
    PropTypes.shape({
        onClick: PropTypes.function,
        onMouseLeave: PropTypes.function,
        href: PropTypes.string,
    }),
    () => null,
]);
export const HOVER_ACTIONS = {
    SHOW: 'SHOW',
    HIDE: 'HIDE',
    TOGGLE: 'TOGGLE',
};
export const PropertyInfo = ({ listing, elmId, isHoverMode = false, className, isCancelled = false, }) => {
    const { total_bedrooms: beds, total_bathrooms: baths, square_footage: sqft, lot_size: lotSize, year_built: yearBuilt, } = listing.primary_property.summary;
    return (_jsxs("div", { className: cx('property-info', className), children: [!isCancelled && (_jsxs("div", { className: cx('primary-info'), children: [!!beds && (_jsxs("div", { className: cx({
                            separator: baths || sqft,
                        }), children: [_jsx("span", { className: cx('value'), "data-elm-id": `card_auction_beds_${elmId}`, children: beds }), ' ', "bd"] })), !!beds && !!baths && (_jsxs("div", { className: cx({ separator: sqft }), children: [_jsx("span", { className: cx('value'), "data-elm-id": `card_auction_baths_${elmId}`, children: baths }), ' ', "ba"] })), !!sqft && (_jsxs("div", { className: cx('truncate'), children: [_jsx("span", { className: cx('value'), "data-elm-id": `card_auction_sqft_${elmId}`, children: Number(sqft).toLocaleString() }), ' ', "sq. ft."] }))] })), isHoverMode && (lotSize || yearBuilt) && (_jsxs("div", { className: cx('secondary-info'), children: [lotSize && (_jsxs("div", { className: cx({ separator: yearBuilt }), children: ["Lot Size:", ' ', _jsx("span", { className: cx('value'), children: `${lotSize} Acres` })] })), yearBuilt && (_jsxs("div", { children: ["Year Built: ", _jsx("span", { className: cx('value'), children: yearBuilt })] }))] }))] }));
};
PropertyInfo.propTypes = {
    listing: PropTypes.object,
    elmId: PropTypes.string,
    isHoverMode: PropTypes.bool,
    className: PropTypes.string,
    isCancelled: PropTypes.bool,
};
export const ListingCardIndicator = ({ listing, className, ...restProps }) => {
    return (_jsx(ListingIndicator, { ...restProps, listing: listing, hideAuctionDate: listing.online_auction_segment?.segment_type ===
            enums.OnlineAuctionSegmentType.SealedBid, className: cx('asset-info', className) }));
};
ListingCardIndicator.propTypes = {
    className: PropTypes.string,
    listing: PropTypes.object,
};
const Prices = ({ prices, elmId }) => prices && prices.length ? (_jsx("div", { className: cx('prices'), "data-elm-id": `card_prices_${elmId}`, children: prices.slice(0, 2).map((price, index) => price.amount ? (_jsxs("div", { className: cx('price'), "data-elm-id": `card_price_${index}`, children: [_jsxs("label", { "data-elm-id": 'card_price_label', children: [price.label, ":", _jsx("span", { className: cx('dots'), children: '.'.repeat(50) })] }), _jsx("div", { className: cx('amount'), "data-elm-id": 'card_price_amount', children: price.amount })] }, index)) : null) })) : null;
Prices.propTypes = {
    prices: PropTypes.array,
    elmId: PropTypes.string,
};
const FooterLower = ({ listing, elmId, linkProps }) => {
    return (_jsxs("div", { "data-elm-id": `footer-lower_${elmId}`, className: cx('footer-lower'), children: [_jsx("div", { className: cx('opening-bid'), children: listing.auction.starting_bid && (_jsxs("div", { children: ["Opening Bid:", _jsx("p", { className: cx('amount'), children: getOpeningBidValue(listing) })] })) }), _jsx("div", { children: _jsx(Button, { size: 'small', role: 'primary', onClick: linkProps.onClick, link: linkProps.href, children: "View Now" }) })] }));
};
FooterLower.propTypes = {
    listing: PropTypes.object,
    elmId: PropTypes.string,
    linkProps: linkPropType,
};
export const listingAddressPropTypes = PropTypes.shape({
    street_description: PropTypes.string,
    municipality: PropTypes.string,
    country_primary_subdivision: PropTypes.string,
    country_secondary_subdivision: PropTypes.string,
    postal_code: PropTypes.string,
});
export const listingPropType = PropTypes.shape({
    listing_id: PropTypes.string.isRequired,
    primary_photo: PropTypes.string,
    listing_photos_count: PropTypes.number,
    listing_page_path: PropTypes.string.isRequired,
    reserve_price: PropTypes.number,
    is_hot: PropTypes.bool,
    listing_status_label: PropTypes.string.isRequired,
    listing_configuration: PropTypes.shape({
        product_type: PropTypes.string.isRequired,
        broker_commission: PropTypes.number,
        financing_available: PropTypes.bool,
        buyer_premium_available: PropTypes.bool,
        interior_access_allowed: PropTypes.bool,
        occupancy_status: PropTypes.string,
        is_reserve_displayed: PropTypes.bool,
        mlh_seller_code: PropTypes.string,
        asset_type: PropTypes.string,
        is_direct_offer_enabled: PropTypes.bool,
        is_third_party_online: PropTypes.bool,
    }),
    listing_summary: PropTypes.shape({
        is_remote_bid_enabled: PropTypes.bool,
        is_remote_before_and_during_auction_enabled: PropTypes.bool,
        show_opening_bid: PropTypes.bool,
    }),
    event: PropTypes.shape({
        event_code: PropTypes.string,
    }),
    seller_property: listingAddressPropTypes,
    primary_property: PropTypes.shape({
        property_id: PropTypes.string,
        summary: PropTypes.shape({
            street_address: PropTypes.string,
            total_bedrooms: PropTypes.number,
            total_bathrooms: PropTypes.number,
            square_footage: PropTypes.number,
            lot_size: PropTypes.number,
            year_built: PropTypes.number,
            valuation: PropTypes.number,
            structure_type_code: PropTypes.string,
        }),
        is_currently_saved: PropTypes.bool,
        is_newly_listed: PropTypes.bool,
        current_user_tracking_state: PropTypes.shape({
            is_seen: PropTypes.bool,
            is_updated: PropTypes.bool,
        }),
    }),
    auction: PropTypes.shape({
        start_date: PropTypes.string,
        end_date: PropTypes.string,
        starting_bid: PropTypes.number,
        is_online: PropTypes.bool,
        bid_instruction: PropTypes.shape({
            nos_amount: PropTypes.number,
        }),
    }),
    parties: PropTypes.arrayOf(PropTypes.shape({
        party_role: PropTypes.string,
        party_name: PropTypes.string,
    })),
    external_information: PropTypes.shape({
        collateral: PropTypes.shape({
            summary: PropTypes.arrayOf(PropTypes.shape({
                estimate: PropTypes.number,
                high: PropTypes.number,
                type: PropTypes.string,
            })),
        }),
    }),
});
export const PropertyType = ({ listing, featureFlags, isHorizontalView }) => {
    const propertyTypeInfo = blueprint.listing.utils.getPropertyTypeInfo({
        listing,
        featureFlags,
    });
    if (!propertyTypeInfo) {
        return null;
    }
    const { combinedTextArray, offerType, structureInfoCombined } = propertyTypeInfo;
    return (_jsxs("div", { className: cx('property-type', { 'horizontal-view': isHorizontalView }), title: structureInfoCombined, children: [offerType && (_jsx("span", { children: _jsx("span", { children: _jsx("b", { children: offerType }) }) })), combinedTextArray.map((text, index) => (_jsxs(React.Fragment, { children: [index > 0 || offerType ? ' | ' : '', text] }, index)))] }));
};
PropertyType.propTypes = {
    listing: listingPropType,
    featureFlags: PropTypes.object,
    isHorizontalView: PropTypes.bool,
};
export const LeftSidePropertyValue = ({ listing, isPopup = false }) => {
    const displayedValues = getDisplayedValue(listing);
    const displayReserveMet = shouldDisplayReserveMet(listing);
    return (_jsxs("div", { className: cx({ 'popup-property-value': isPopup }), children: [_jsx("div", { className: cx('labels-container'), children: _jsx("span", { className: cx('amount-label'), children: displayedValues.label }) }), _jsxs("div", { className: cx('labels-container'), children: [displayedValues.amount ? (_jsx("div", { title: displayedValues.amount, className: cx('amount', {
                            muted: displayedValues.amount === MISSING_VALUE_LABEL,
                        }), children: displayedValues.amount })) : null, displayReserveMet && !isPopup && (_jsx("div", { title: 'Reserve Met', className: cx('reserve-met'), children: "Reserve Met" }))] })] }));
};
LeftSidePropertyValue.propTypes = {
    listing: listingPropType,
    isPopup: PropTypes.bool,
};
export const PropertyValue = ({ listing, elmId, isHorizontalView = false, featureFlags = {}, }) => {
    const shouldDisplaySmartSaleLogo = featureFlags['fe.resi.enable-private-seller'] &&
        blueprint.listing.utils.isSmartSale(listing) &&
        !blueprint.listing.utils.isSparkOffer(listing);
    const valuationContent = (_jsxs("div", { children: [_jsx("span", { className: cx('amount-label'), children: "Est. Resale Value" }), listing.primary_property?.summary?.valuation ? (_jsx("p", { className: cx('amount'), children: valuationEstimate(listing) })) : (_jsx("p", { className: cx('amount', 'muted'), children: MISSING_VALUE_LABEL }))] }));
    return (_jsxs("div", { className: cx('property-value-container', {
            'horizontal-view': isHorizontalView,
        }), children: [_jsx("div", { className: cx('opening-bid'), "data-elm-id": `property_value_left_column_${elmId}`, children: _jsx(LeftSidePropertyValue, { listing: listing }) }), _jsx("div", { className: cx('reserve-value'), "data-elm-id": `property_value_right_column_${elmId}`, children: shouldDisplaySmartSaleLogo ? (_jsx("div", { className: cx('smart-sale-logo-container'), children: _jsx("img", { src: SmartSaleLogo }) })) : (valuationContent) })] }));
};
PropertyValue.propTypes = {
    listing: listingPropType,
    elmId: PropTypes.string,
    featureFlags: PropTypes.object,
    isHorizontalView: PropTypes.bool,
};
export const AddressInfoLine = ({ address = [], elmId, className, isCancelled, }) => {
    return (_jsx("div", { children: _jsx("div", { "data-elm-id": `address_line_${elmId}`, className: cx('property-address-line', className), children: _jsx(AddressInfo, { address: address, elmId: elmId, isCancelled: isCancelled }) }) }));
};
AddressInfoLine.propTypes = {
    address: PropTypes.arrayOf(PropTypes.string),
    elmId: PropTypes.string,
    className: PropTypes.string,
    isCancelled: PropTypes.bool,
};
export const CardImage = ({ listing, elmId, showSave, showImageCount = true, onSaveToggle, stopPropagationClick, loading, maxBadges, isCancelled, isHorizontalView = false, className = '', featureFlags = {}, verifiedImage, updateVerifiedImage, viewingFrom = graph.enums.CurrentTargetAudience.Buyer, badgeClassName = '', }) => {
    const altTextImage = getAddressLine(listing.seller_property);
    const auxPhoto = useBackgroundImageWithFallback(listing.primary_photo, featureFlags['fe.resi.enabled-photo-enhancement'], verifiedImage, () => updateVerifiedImage && updateVerifiedImage(listing.listing_id, null));
    let primaryPhoto;
    if (verifiedImage) {
        primaryPhoto = verifiedImage;
    }
    else {
        primaryPhoto = auxPhoto;
    }
    useEffect(() => {
        if (updateVerifiedImage && auxPhoto !== verifiedImage) {
            updateVerifiedImage(listing.listing_id, auxPhoto);
        }
    }, [auxPhoto, verifiedImage, updateVerifiedImage]);
    const listingPhotoUrl = formatListingImgUrl(primaryPhoto, {
        width: LISTING_CARD_PHOTO_WIDTH,
    });
    return loading ? (_jsx("div", { className: cx('property-card-image-container', {
            'horizontal-view': isHorizontalView,
        }, className), children: _jsx(Placeholder, { fluid: true, style: { height: '100%' }, children: _jsx(Placeholder.Image, {}) }) })) : (_jsxs("div", { className: cx('property-card-image-container', {
            'horizontal-view': isHorizontalView,
        }, className), children: [_jsx("span", { className: cx('property-card-image-background'), style: {
                    backgroundImage: `url(${listingPhotoUrl})`,
                }, role: 'img', "aria-label": altTextImage }), _jsx(ListingBadgesTags, { listing: listing, elmId: elmId, isHorizontalView: isHorizontalView, maxBadges: maxBadges, viewingFrom: viewingFrom, className: badgeClassName }), showSave && (_jsx("span", { className: cx('save-button'), onClick: stopPropagationClick, children: _jsx(PropertySaveIndicator, { elmId: elmId, property: listing.primary_property, onSaveToggle: onSaveToggle, isCancelled: isCancelled }) })), showImageCount && listing.listing_photos_count > 1 ? (_jsx("div", { className: cx('photo-count-badge'), children: `${listing.listing_photos_count} images` })) : null] }));
};
CardImage.propTypes = {
    listing: listingPropType,
    elmId: PropTypes.string,
    showSave: PropTypes.bool,
    showImageCount: PropTypes.bool,
    onSaveToggle: PropTypes.func,
    stopPropagationClick: PropTypes.func,
    isHorizontalView: PropTypes.bool,
    loading: PropTypes.bool,
    maxBadges: PropTypes.number,
    isCancelled: PropTypes.bool,
    className: PropTypes.string,
    featureFlags: PropTypes.object,
    verifiedImage: PropTypes.string,
    updateVerifiedImage: PropTypes.func,
    viewingFrom: PropTypes.oneOf(Object.values(graph.enums.CurrentTargetAudience)),
    badgeClassName: PropTypes.string,
};
//# sourceMappingURL=cardParts.js.map